import PropTypes from "prop-types";
import BaseNode from "./BaseNode";
import { RealisticHighbayLamp } from "./realistic";

/**
 * HighbayLampNode – Highbay lamp visualization.
 *
 * Supports:
 * - Realistic 3D SCADA design with illuminated LED diode matrix & downward light cone
 * - Premium glassmorphism layout
 * - Classic industrial SVG layout
 */
const HighbayLampNode = (props) => {
  return (
    <BaseNode {...props}>
      {({ isRunning, isOffline, statusColor, speedScale, ratio, designType, color, opacity, liveValue, unit, width, height }) => {
        if (designType === "realistic") {
          return (
            <RealisticHighbayLamp
              liveValue={liveValue}
              unit={unit || "W"}
              isRunning={isRunning}
              isOffline={isOffline}
              statusColor={statusColor}
              speedScale={speedScale}
              ratio={ratio}
              opacity={opacity}
              width={width}
              height={height}
            />
          );
        }

        const isOn = isRunning;
        const dotColor =
          statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#ef4444" : "#94a3b8";

        // Glow animation speed — scales with live value proximity to maxLevel
        const glowDuration = isOn ? `${Math.max(0.6, 2.0 / (speedScale || 1))}s` : "0s";
        const showOldDesign = designType === "new_design" || designType === "classic";

        return (
          <svg
            width="65"
            height="75"
            viewBox="0 0 65 75"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              opacity,
              filter: isOffline ? "grayscale(100%) opacity(0.6)" : "none",
              overflow: "visible",
              transition: "all 0.3s ease",
            }}
          >
            {!showOldDesign ? (
              // ─── NEW PREMIUM GLASSMORPHIC DESIGN ───
              <>
                <defs>
                  {/* Aluminum casing gradient */}
                  <linearGradient id="fixtureMetal" x1="0" y1="0" x2="65" y2="0">
                    <stop offset="0%" stopColor="#475569" />
                    <stop offset="30%" stopColor="#94a3b8" />
                    <stop offset="50%" stopColor="#cbd5e1" />
                    <stop offset="70%" stopColor="#94a3b8" />
                    <stop offset="100%" stopColor="#334155" />
                  </linearGradient>
                  {/* Light beam cone gradient */}
                  <linearGradient id="lightCone" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity="0.65" />
                    <stop offset="40%" stopColor={color} stopOpacity="0.35" />
                    <stop offset="100%" stopColor={color} stopOpacity="0.02" />
                  </linearGradient>
                  <filter id="lensGlow" x="-30%" y="-30%" width="160%" height="160%">
                    <feGaussianBlur stdDeviation="3.5" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                </defs>

                {/* Hanging loop */}
                <circle cx="32" cy="7" r="4.5" fill="none" stroke="#64748b" strokeWidth="1.5" />
                <rect x="30" y="10.5" width="4" height="4" fill="#64748b" />

                {/* Heat sink (cooling fins on top of the driver box) */}
                <rect x="22" y="14" width="20" height="3" rx="0.5" fill="#334155" />
                <rect x="20" y="18" width="24" height="3" rx="0.5" fill="#334155" />
                <rect x="18" y="22" width="28" height="3" rx="0.5" fill="#334155" />

                {/* Reflector Bell Dome */}
                <path d="M22,25 L42,25 L50,42 L14,42 Z" fill="url(#fixtureMetal)" stroke="#334155" strokeWidth="1" />
                {/* Vertical ribs on the reflector */}
                <line x1="26" y1="25" x2="20" y2="42" stroke="#475569" strokeWidth="1" />
                <line x1="32" y1="25" x2="32" y2="42" stroke="#475569" strokeWidth="1" />
                <line x1="38" y1="25" x2="44" y2="42" stroke="#475569" strokeWidth="1" />

                {/* LED board base (inside reflector mouth) */}
                <ellipse cx="32" cy="42" rx="18" ry="4" fill="#0f172a" />

                {/* LED Chip Array (Yellow light emitting diodes) */}
                <g fill={isOn ? "#fef08a" : "#ca8a04"}>
                  <circle cx="20" cy="42" r="1.2" />
                  <circle cx="26" cy="42.5" r="1.2" />
                  <circle cx="32" cy="43" r="1.5" />
                  <circle cx="38" cy="42.5" r="1.2" />
                  <circle cx="44" cy="42" r="1.2" />
                </g>

                {/* High-visibility Light Beam Cone when ON */}
                {isOn && (
                  <>
                    {/* Light cone polygon */}
                    <polygon points="14,42 50,42 62,72 2,72" fill="url(#lightCone)" />

                    {/* Dynamic light ray lines */}
                    <line x1="32" y1="42" x2="32" y2="70" stroke={color} strokeWidth="3.5" opacity="0.5" filter="url(#lensGlow)">
                      <animate attributeName="opacity" values="0.2;0.8;0.2" dur="2s" repeatCount="indefinite" />
                    </line>
                    <line x1="24" y1="42" x2="14" y2="70" stroke={color} strokeWidth="2" opacity="0.3" filter="url(#lensGlow)">
                      <animate attributeName="opacity" values="0.5;0.1;0.5" dur="2.5s" repeatCount="indefinite" />
                    </line>
                    <line x1="40" y1="42" x2="50" y2="70" stroke={color} strokeWidth="2" opacity="0.3" filter="url(#lensGlow)">
                      <animate attributeName="opacity" values="0.1;0.6;0.1" dur="1.8s" repeatCount="indefinite" />
                    </line>

                    {/* Glowing floating dust particles inside light cone */}
                    <circle cx="20" cy="55" r="1.8" fill="#ffffff" opacity="0.7">
                      <animate attributeName="cy" values="68;43;68" dur="2.5s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values="0.1;0.9;0.1" dur="2.5s" repeatCount="indefinite" />
                    </circle>
                    <circle cx="44" cy="62" r="1.5" fill="#ffffff" opacity="0.6">
                      <animate attributeName="cy" values="70;47;70" dur="1.8s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values="0.9;0.1;0.9" dur="1.8s" repeatCount="indefinite" />
                    </circle>
                    <circle cx="32" cy="50" r="2.2" fill="#ffffff" opacity="0.5">
                      <animate attributeName="cy" values="60;42;60" dur="3.5s" repeatCount="indefinite" />
                      <animate attributeName="opacity" values="0.2;0.8;0.2" dur="3.5s" repeatCount="indefinite" />
                    </circle>

                    {/* Lens Flare Glow */}
                    <ellipse cx="32" cy="42" rx="28" ry="8" fill={color} opacity="0.45" filter="url(#lensGlow)">
                      <animate attributeName="opacity" values="0.2;0.6;0.2" dur="1.2s" repeatCount="indefinite" />
                    </ellipse>
                  </>
                )}

                {/* Status dot on top right */}
                <circle cx="54" cy="9" r="5" fill={dotColor} stroke="#ffffff" strokeWidth="1.5" filter={isOn ? "url(#lensGlow)" : "none"}>
                  {isOn && (
                    <animate attributeName="opacity" values="1;0.5;1" dur="1.2s" repeatCount="indefinite" />
                  )}
                </circle>
              </>
            ) : (
              // ─── CLASSIC INDUSTRIAL SVG DESIGN ───
              <>
                {/* Lamp housing (trapezoid) */}
                <path
                  d="M15 10 L45 10 L50 35 L10 35 Z"
                  fill={isOn ? `${color}10` : `${color}05`}
                  stroke={isOn ? color : "#64748b"}
                  strokeWidth="2"
                  strokeLinejoin="round"
                />

                {/* Mounting bracket */}
                <rect x="25" y="2" width="10" height="10" rx="2"
                  fill={`${color}15`}
                  stroke={color} strokeWidth="1.5"
                />
                <line x1="30" y1="0" x2="30" y2="2" stroke={color} strokeWidth="2" />

                {/* Lens/diffuser */}
                <rect x="12" y="33" width="36" height="5" rx="2"
                  fill={isOn ? `${color}60` : `${color}15`}
                  stroke={color} strokeWidth="1"
                />

                {/* Light glow when on */}
                {isOn && (
                  <g style={{ animation: `live-lamp-glow ${glowDuration} ease-in-out infinite` }}>
                    <path d="M12 38 L0 65 L60 65 L48 38 Z" fill={`${color}08`} />
                    <path d="M16 38 L8 58 L52 58 L44 38 Z" fill={`${color}12`} />
                    <line x1="30" y1="38" x2="30" y2="55" stroke={color} strokeWidth="1" opacity="0.3" />
                    <line x1="20" y1="38" x2="15" y2="55" stroke={color} strokeWidth="0.5" opacity="0.2" />
                    <line x1="40" y1="38" x2="45" y2="55" stroke={color} strokeWidth="0.5" opacity="0.2" />
                  </g>
                )}

                {/* Status dot */}
                <circle cx="50" cy="8" r="4" fill={dotColor} />
              </>
            )}

            {/* Status text - clearly below lamp */}
            <text x="32" y="66" textAnchor="middle" fontSize="7.5" fontWeight="800" fill={dotColor} style={{ letterSpacing: 0.5 }}>
              {isOffline ? "OFFLINE" : (isOn ? "ON" : "OFF")}
            </text>
          </svg>
        );
      }}
    </BaseNode>
  );
};

HighbayLampNode.propTypes = { data: PropTypes.object, selected: PropTypes.bool, id: PropTypes.string };
export default HighbayLampNode;
